
import { Component, Prop, Vue } from 'vue-property-decorator';
import { SupplierModel, IBulkSupplierInfoUpdate } from '@/models/supplier';

interface EditableRow {
  id: number;
  name: string;
  contactEmail: string;
  contactPhone: string;
  address: string;
  originalName: string;
  originalContactEmail: string;
  originalContactPhone: string;
  originalAddress: string;
  isDirty: boolean;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({ name: 'SupplierBulkInfoEditorDialog' })
export default class SupplierBulkInfoEditorDialog extends Vue {
  @Prop({ required: true }) suppliers!: SupplierModel[];

  private rows: EditableRow[] = [];
  private loadError: string | null = null;
  private saving = false;
  private partialErrors: string[] = [];
  private saveError: string | null = null;
  private search = '';
  private showDirtyOnly = false;

  private get dialogMaxWidth(): string {
    return '1200px';
  }

  private get headers() {
    return [
      { text: '', value: 'dirty', width: '28px', sortable: false, align: 'center' },
      { text: 'Name', value: 'name', sortable: false },
      { text: 'Email', value: 'contactEmail', sortable: false },
      { text: 'Phone', value: 'contactPhone', sortable: false, width: '180px' },
      { text: 'Address', value: 'address', sortable: false }
    ];
  }

  private get dirtyRows(): EditableRow[] {
    return this.rows.filter(r => r.isDirty);
  }

  private get dirtyCount(): number {
    return this.dirtyRows.length;
  }

  private get invalidEmailCount(): number {
    return this.dirtyRows.filter(r => !this.isEmailValid(r.contactEmail)).length;
  }

  private get canSave(): boolean {
    return !this.saving && this.dirtyCount > 0 && this.invalidEmailCount === 0;
  }

  private get filteredRows(): EditableRow[] {
    const base = this.showDirtyOnly ? this.dirtyRows : this.rows;
    const q = this.search.trim().toLowerCase();
    if (!q) return base;
    return base.filter(r =>
      r.name.toLowerCase().includes(q)
      || r.contactEmail.toLowerCase().includes(q)
      || r.contactPhone.toLowerCase().includes(q)
      || r.address.toLowerCase().includes(q)
    );
  }

  private get rowCountLabel(): string {
    const total = this.rows.length;
    const shown = this.filteredRows.length;
    if (shown === total) return `${total} ${total === 1 ? 'supplier' : 'suppliers'}`;
    return `${shown} of ${total} suppliers`;
  }

  private get emptyStateText(): string {
    if (this.rows.length === 0) return 'No suppliers selected.';
    if (this.showDirtyOnly && this.dirtyCount === 0) return 'No unsaved changes.';
    if (this.search) return `No suppliers match "${this.search}".`;
    return 'No suppliers to display.';
  }

  private get saveErrorVisible(): boolean {
    return !!this.saveError;
  }
  private set saveErrorVisible(val: boolean) {
    if (!val) this.saveError = null;
  }

  mounted() {
    this.loadRows();
  }

  private loadRows() {
    this.loadError = null;
    const incoming = this.suppliers || [];
    if (incoming.length === 0) {
      this.rows = [];
      this.loadError = 'No suppliers selected. Close this dialog and select at least one supplier to bulk edit.';
      return;
    }
    if (incoming.some(s => s == null || s.id == null)) {
      this.rows = [];
      this.loadError = 'Some selected suppliers are missing an id. Refresh the supplier list and try again.';
      return;
    }
    this.rows = incoming.map((s): EditableRow => ({
      id: s.id,
      name: s.name || '',
      contactEmail: s.contactEmail || '',
      contactPhone: s.contactPhone || '',
      address: s.address || '',
      originalName: s.name || '',
      originalContactEmail: s.contactEmail || '',
      originalContactPhone: s.contactPhone || '',
      originalAddress: s.address || '',
      isDirty: false,
    }));
  }

  private updateRow(rowId: number, field: 'name' | 'contactEmail' | 'contactPhone' | 'address', value: string) {
    const row = this.rows.find(r => r.id === rowId);
    if (!row) return;
    row[field] = value;
    row.isDirty =
      row.name !== row.originalName
      || row.contactEmail !== row.originalContactEmail
      || row.contactPhone !== row.originalContactPhone
      || row.address !== row.originalAddress;
  }

  private revertAll() {
    this.rows.forEach(row => {
      if (row.isDirty) {
        row.name = row.originalName;
        row.contactEmail = row.originalContactEmail;
        row.contactPhone = row.originalContactPhone;
        row.address = row.originalAddress;
        row.isDirty = false;
      }
    });
    this.showDirtyOnly = false;
  }

  private isEmailValid(email: string): boolean {
    if (!email) return false;
    return EMAIL_RE.test(email);
  }

  // Build the bulk-update payload: only dirty rows, only changed fields.
  // Empty strings are preserved (not dropped) so the user can clear phone/address.
  private buildSavePayload(): IBulkSupplierInfoUpdate[] {
    return this.dirtyRows.map(r => {
      const update: IBulkSupplierInfoUpdate = { id: r.id };
      if (r.name !== r.originalName) update.name = r.name;
      if (r.contactEmail !== r.originalContactEmail) update.contactEmail = r.contactEmail;
      if (r.contactPhone !== r.originalContactPhone) update.contactPhone = r.contactPhone;
      if (r.address !== r.originalAddress) update.address = r.address;
      return update;
    });
  }

  private handleSaveResult(results: any[], errors: any[]) {
    const successIds = new Set(results.map((r: any) => r.id));
    this.rows.forEach(row => {
      if (successIds.has(row.id)) {
        row.originalName = row.name;
        row.originalContactEmail = row.contactEmail;
        row.originalContactPhone = row.contactPhone;
        row.originalAddress = row.address;
        row.isDirty = false;
      }
    });
    if (errors.length > 0) {
      this.partialErrors = errors.map((e: any) => {
        const lookupId = e.update?.id ?? e.id;
        const row = this.rows.find(r => r.id === lookupId);
        // Use originalName (stable, pre-edit) so the banner identifies the row
        // by the name the user knew it by, not their possibly-invalid edit.
        return row ? row.originalName : (e.error || 'Unknown supplier');
      });
    }
  }

  private async save() {
    if (!this.canSave) return;
    this.saving = true;
    this.partialErrors = [];
    try {
      const payload = this.buildSavePayload();
      const response = await SupplierModel.bulkUpdateInfo(payload);
      const results = response?.results || [];
      const errors = response?.errors || [];
      this.handleSaveResult(results, errors);
      if (errors.length === 0) {
        this.$emit('saved');
        this.$emit('close');
      } else if (this.dirtyCount === 0) {
        this.showDirtyOnly = false;
      }
    } catch {
      this.saveError = 'Failed to save changes. Please try again.';
    } finally {
      this.saving = false;
    }
  }

  private onCancel() {
    this.$emit('close');
  }

  private getRowClass(item: EditableRow): string {
    return item.isDirty ? 'row--dirty' : '';
  }
}
